// pages/ProjectDetail.js
import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Calendar, Check, ArrowLeft } from 'lucide-react';

const ProjectDetail = () => {
  const { slug } = useParams();

  const projectData = {
    'mv-switchgear-installation': {
      title: 'MV Switchgear Installation',
      location: 'Nairobi, Kenya',
      year: '2024',
      scope: '11kV Switchgear with Ring Main Unit',
      image: '/images/projects/mv-switchgear-nairobi.jpg',
      description: 'Complete installation of medium voltage switchgear with Ring Main Unit (RMU) for a large industrial facility in Nairobi. The project involved supplying, installing, and commissioning 11kV switchgear panels with advanced protection systems.',
      challenges: 'Limited space in existing electrical room, tight timeline of 4 weeks, complex integration with existing infrastructure, and stringent safety requirements.',
      solutions: 'Custom compact switchgear design, efficient project management with phased installation, seamless integration with existing systems, and comprehensive safety protocols.',
      results: 'System operational within 3.5 weeks, 99.9% reliability achieved, improved power distribution, and enhanced safety features.'
    },
    'supply-distribution-board': {
      title: 'Supply of Distribution Board',
      location: 'Nairobi Industrial Area',
      year: '2024',
      scope: 'Custom Distribution Boards',
      image: '/images/projects/db-manufacturing-nairobi.jpg',
      description: 'Design, manufacture, and supply of 50 custom distribution boards for a new industrial facility in Nairobi. Each board was customized to meet specific load requirements and safety standards.',
      challenges: 'High volume production, strict quality standards, tight delivery schedule of 6 weeks, and varying specifications for different sections of the facility.',
      solutions: 'Streamlined production process with quality control checkpoints, efficient logistics planning, and modular design approach for flexibility.',
      results: '100% on-time delivery, zero defects reported, client satisfaction, and successful integration with existing electrical infrastructure.'
    },
    'control-panel-installation': {
      title: 'Installation of Control Panel',
      location: 'Industrial Area, Nairobi',
      year: '2024',
      scope: 'Automation Control Panels',
      image: '/images/projects/control-panel-nairobi.jpg',
      description: 'Installation of custom automation control panels with PLC integration for a manufacturing plant in Nairobi. The control panels manage complex industrial processes with precision and reliability.',
      challenges: 'Complex automation requirements, integration with multiple existing systems, and minimal production downtime during installation.',
      solutions: 'Custom PLC programming, modular panel design, thorough testing before installation, and phased implementation to minimize disruption.',
      results: 'Fully automated system with 30% increase in productivity, reduced operator intervention, improved quality control, and enhanced safety features.'
    },
    'pv-grid-tie-solar': {
      title: 'PV Grid Tie Solar Installation',
      location: 'Nakuru, Kenya',
      year: '2025',
      scope: 'Grid-Tied Solar System with Combiner Boards',
      image: '/images/projects/commercial-solar-nakuru.jpg',
      description: 'Installation of a 500kW grid-tied solar PV system with solar combiner boards for a commercial facility in Nakuru. The system generates clean energy and reduces electricity costs significantly.',
      challenges: 'Large-scale installation, grid integration requirements, minimal disruption to ongoing operations, and compliance with utility regulations.',
      solutions: 'Phased installation approach, advanced grid-tied inverters, professional project management, and comprehensive testing before grid connection.',
      results: '60% reduction in electricity costs, clean energy generation, rapid ROI within 3 years, and successful grid integration.'
    },
    'solar-combiner-board': {
      title: 'Solar Combiner Board',
      location: 'Nairobi, Kenya',
      year: '2024',
      scope: 'Solar PV Combiner & Distribution',
      image: '/images/projects/solar-combiner-board.jpg',
      description: 'Design and installation of solar combiner boards for a large-scale solar PV system in Nairobi. The combiner boards consolidate multiple PV strings for efficient power distribution.',
      challenges: 'High DC voltage requirements, proper cable management, safety compliance, and integration with existing solar infrastructure.',
      solutions: 'Custom combiner board design, high-quality components, professional installation, and comprehensive testing.',
      results: 'Efficient solar power distribution, improved system monitoring, enhanced safety features, and successful project completion.'
    },
    'lv-main-switchboard': {
      title: 'LV Main Switchboard',
      location: 'Westlands, Nairobi',
      year: '2025',
      scope: '1600A LV Main Switchboard',
      image: '/images/projects/lv-switchboard-nairobi.jpg',
      description: 'Installation of a 1600A low voltage main switchboard for a commercial building in Westlands, Nairobi. The switchboard distributes power to multiple floors and critical systems.',
      challenges: 'Space constraints in the electrical room, high power requirements, safety compliance, and integration with existing electrical systems.',
      solutions: 'Compact switchboard design, advanced protection systems, safety certifications, and professional installation.',
      results: 'Reliable power distribution, improved safety, client satisfaction, and successful building handover.'
    },
    'lv-distribution': {
      title: 'LV Distribution System',
      location: 'Nairobi, Kenya',
      year: '2024',
      scope: 'Complete LV Distribution Network',
      image: '/images/projects/lv-distribution.jpg',
      description: 'Design and installation of a complete low voltage distribution network for an industrial park in Nairobi. The system includes distribution boards, cabling, and protection systems.',
      challenges: 'Large-scale distribution network, coordination with multiple stakeholders, and compliance with industry standards.',
      solutions: 'Comprehensive design, professional installation, quality control, and thorough testing.',
      results: 'Efficient power distribution, reduced losses, improved reliability, and client satisfaction.'
    },
    'ring-main-unit': {
      title: 'Ring Main Unit Installation',
      location: 'Nairobi, Kenya',
      year: '2025',
      scope: 'RMU Switchgear Installation',
      image: '/images/projects/ring-main-unit.jpg',
      description: 'Installation of a Ring Main Unit (RMU) for improved power distribution reliability in a commercial complex in Nairobi. The RMU provides enhanced protection and switching capabilities.',
      challenges: 'Integration with existing MV network, space constraints, and safety requirements.',
      solutions: 'Custom RMU design, professional installation, comprehensive testing, and safety certifications.',
      results: 'Improved power distribution reliability, enhanced safety, reduced downtime, and client satisfaction.'
    },
    'ht-panel-repair-eakip': {
      title: 'Repair and Maintenance of 11kV HT Panel',
      location: 'East African Kidney Institute (EAKIP)',
      year: '2025',
      scope: '11kV HT Panel Repair & Maintenance',
      image: '/images/projects/ht-panel-repair-eakip.jpg',
      description: 'Comprehensive repair and maintenance of the 11kV High Tension (HT) panel at the East African Kidney Institute. The project involved complete overhaul, testing, and restoration of the critical power distribution system.',
      challenges: 'Critical medical facility requiring uninterrupted power, aging equipment with obsolete parts, strict safety protocols, and minimal downtime window.',
      solutions: 'Detailed assessment and planning, sourcing of compatible replacement parts, phased maintenance approach, and rigorous testing protocols.',
      results: 'Fully restored 11kV HT panel with improved reliability, enhanced safety features, zero downtime impact on hospital operations, and extended equipment lifespan.'
    }
  };

  const project = projectData[slug];

  if (!project) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">Project not found</h1>
          <Link to="/projects" className="text-[#006D77] hover:underline mt-2 inline-block text-sm">
            ← Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16">
      <section className="py-10 bg-gradient-to-r from-[#006D77] to-[#008B8B] text-white">
        <div className="container mx-auto px-4 md:px-6">
          <Link to="/projects" className="inline-flex items-center text-white/80 hover:text-white text-sm mb-3 transition-colors">
            <ArrowLeft size={16} className="mr-2" />
            Back to Projects
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold">{project.title}</h1>
          <div className="flex flex-wrap gap-4 mt-3">
            <span className="flex items-center gap-2 text-white/80 text-sm">
              <MapPin size={16} />
              {project.location}
            </span>
            <span className="flex items-center gap-2 text-white/80 text-sm">
              <Calendar size={16} />
              {project.year}
            </span>
          </div>
        </div>
      </section>

      <section className="py-8">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <img 
                src={project.image}
                alt={project.title}
                className="w-full h-64 object-cover rounded-lg shadow"
                onError={(e) => {
                  e.target.src = `https://picsum.photos/seed/${slug}/800/600`;
                }}
              />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 mb-2">Project Overview</h2>
              <p className="text-gray-600 text-sm leading-relaxed">{project.description}</p>
              <p className="text-gray-600 text-sm mt-2 font-medium">Scope: {project.scope}</p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4 mb-6">
            <div className="bg-gray-50 rounded-lg p-5">
              <h3 className="font-bold text-gray-900 text-sm mb-2">Challenge</h3>
              <p className="text-gray-600 text-sm">{project.challenges}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-5">
              <h3 className="font-bold text-gray-900 text-sm mb-2">Solution</h3>
              <p className="text-gray-600 text-sm">{project.solutions}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-5">
              <h3 className="font-bold text-gray-900 text-sm mb-2">Result</h3>
              <p className="text-gray-600 text-sm">{project.results}</p>
            </div>
          </div>

          <div className="bg-gradient-to-r from-[#006D77] to-[#008B8B] rounded-lg p-5 text-center text-white">
            <h3 className="text-lg font-bold mb-1">Need a Similar Project?</h3>
            <p className="text-white/80 text-sm mb-3">Let's discuss your electrical project needs</p>
            <Link to="/contact" className="inline-block bg-white text-[#006D77] px-5 py-1.5 rounded-full font-semibold hover:bg-gray-100 transition-all text-sm">
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProjectDetail;
